import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { calculateSeasonAverages, formatAverage, getMeetCountLabel } from '../utils/seasonAverages';
import { getGroupLabel, sortGroupKeys } from '../utils/meetResultsParser';
import { region1AllotmentTables } from '../data/allotmentTables';

const SeasonAverages = () => {
  const [meets, setMeets] = useState([]);
  const [averages, setAverages] = useState(null);
  const [selectedYear, setSelectedYear] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadMeetsAndCalculateAverages();
  }, []);

  const loadMeetsAndCalculateAverages = async () => {
    setLoading(true);
    setError(null);

    try {
      const meetsRef = collection(db, 'meets');
      const q = query(meetsRef, orderBy('date', 'asc'));
      const snapshot = await getDocs(q);

      const meetsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (meetsData.length === 0) {
        setError('No meets found. Please upload meets first.');
        setLoading(false);
        return;
      }

      setMeets(meetsData);

      const years = [...new Set(
        meetsData
          .map((meet) => new Date(meet.date))
          .filter((date) => !Number.isNaN(date.getTime()))
          .map((date) => String(date.getFullYear()))
      )].sort((a, b) => Number(b) - Number(a));

      const defaultYear = years[0] || '';
      setSelectedYear(defaultYear);

      const meetsForYear = defaultYear
        ? meetsData.filter((meet) => String(new Date(meet.date).getFullYear()) === defaultYear)
        : meetsData;

      const calculatedAverages = calculateSeasonAverages(meetsForYear);
      setAverages(calculatedAverages);
    } catch (err) {
      console.error('Error loading meets:', err);
      setError('Failed to load meets data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!meets.length || !selectedYear) return;

    const meetsForYear = meets.filter(
      (meet) => String(new Date(meet.date).getFullYear()) === selectedYear
    );

    if (meetsForYear.length === 0) {
      setAverages(null);
      return;
    }

    setAverages(calculateSeasonAverages(meetsForYear));
  }, [meets, selectedYear]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-xl text-gray-600 dark:text-gray-400">Loading season data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-8 max-w-md">
          <p className="text-red-600 dark:text-red-400 text-center">{error}</p>
          <button
            onClick={() => window.location.href = '/admin/meets'}
            className="mt-4 w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
          >
            Go to Meet Manager
          </button>
        </div>
      </div>
    );
  }

  const seasonYears = [...new Set(
    meets
      .map((meet) => new Date(meet.date))
      .filter((date) => !Number.isNaN(date.getTime()))
      .map((date) => String(date.getFullYear()))
  )].sort((a, b) => Number(b) - Number(a));

  const selectedSeasonMeets = selectedYear
    ? meets.filter((meet) => String(new Date(meet.date).getFullYear()) === selectedYear)
    : meets;
  const allotmentTable = region1AllotmentTables[selectedYear];

  if (!averages) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-8 max-w-md">
          <p className="text-yellow-700 dark:text-yellow-300 text-center">No meets found for the selected season.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
            Arizona Season Averages - Predicted All-Stars
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {selectedYear} Season - Based on {selectedSeasonMeets.length} meet{selectedSeasonMeets.length !== 1 ? 's' : ''} (Arizona teams only)
          </p>
          <div className="mt-4 flex items-center justify-center gap-3">
            <label htmlFor="season-year" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Season Year
            </label>
            <select
              id="season-year"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              {seasonYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {selectedSeasonMeets.map((meet) => (
              <span
                key={meet.id}
                className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm font-medium"
              >
                {meet.name} ({new Date(meet.date).toLocaleDateString()})
              </span>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Season Summary
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Total Gymnasts</p>
                <p className="text-3xl font-bold text-blue-900 dark:text-blue-300">{averages.totalGymnasts}</p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                <p className="text-sm text-green-600 dark:text-green-400 font-medium">Categories</p>
                <p className="text-3xl font-bold text-green-900 dark:text-green-300">
                  {Object.keys(averages.groups).length}
                </p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">Meets Tracked</p>
                <p className="text-3xl font-bold text-purple-900 dark:text-purple-300">{selectedSeasonMeets.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Region 1 Allotment Table */}
        {allotmentTable && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {allotmentTable.title}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Source: {allotmentTable.source} | As of {new Date(allotmentTable.asOf).toLocaleDateString()}
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Level</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Age Group</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Allotment</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">At States</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">AZ</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {allotmentTable.rows.map((row) => (
                    <tr key={`${row.level}-${row.ageGroup}`}>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{row.level}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{row.ageGroup}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{row.allotment}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{row.competingAtStates}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{row.allQualify ? 'All Qualify' : row.az}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white" colSpan={2}>Totals</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">{allotmentTable.totals.allotment}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">{allotmentTable.totals.competingAtStates}</td>
                    <td className="px-4 py-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* All-Stars by Group */}
        <div className="space-y-6">
          {sortGroupKeys(averages.groups).map((groupKey) => {
            const group = averages.groups[groupKey];
            return (
              <div key={groupKey} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                  {getGroupLabel(group.level, group.division)} - Predicted Top 10 All-Stars
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Total Athletes: {group.totalAthletes}
                </p>

                {/* Top 10 Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Rank
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Gymnast
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Team
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Average Score
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Highest
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Lowest
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Meets
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {group.top10.map((athlete, index) => (
                        <tr
                          key={index}
                          className={`${
                            index < 3 ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''
                          } hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors`}
                        >
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {index === 0 && <span className="text-2xl mr-2">🥇</span>}
                              {index === 1 && <span className="text-2xl mr-2">🥈</span>}
                              {index === 2 && <span className="text-2xl mr-2">🥉</span>}
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {index + 1}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {athlete.name}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {athlete.team}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                              {formatAverage(athlete.average)}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm text-green-600 dark:text-green-400">
                              {formatAverage(athlete.highest)}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {formatAverage(athlete.lowest)}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {getMeetCountLabel(athlete.meetCount, selectedSeasonMeets.length)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SeasonAverages;
